import React, { useCallback } from 'react';
import ButtonComponent from 'components/button/buttonComponent';
import Spinner from 'components/Spinner';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { usePreviousAppointmentSummaries } from 'hooks/api/useGetPastAppointment';
import { MY_APPOINTMENTS_MY_VISIT_SUMMARY } from 'utilities/routes';
import { formatDate } from '../../../../components/datePicker/DatePicker';
import debounce from 'lodash/debounce';

interface PreviousAppointmentSummaryProps {
  patientCodexId: string;
}

export const PreviousAppointmentSummaryBlock: React.FC<
  PreviousAppointmentSummaryProps
> = ({ patientCodexId }) => {
  const { appointments, loading, hasMore, filter, loadMore } =
    usePreviousAppointmentSummaries(patientCodexId);

  const handleClickViewSummary = (id: string) => {
    window.open(`${MY_APPOINTMENTS_MY_VISIT_SUMMARY}/${id}`, '_blank');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceLoadMore = useCallback(debounce(loadMore, 300), [loadMore]);

  const loaderRef = useInfiniteScroll(() => {
    if (!loading && hasMore) {
      debounceLoadMore();
    }
  });

  if (loading && !+filter.offset) return null;

  if (!appointments.length) {
    return (
      <div className="flex w-full h-full gap-2.5 py-2.5">
        <p className="m-auto text-med-gray-3 font-medium">
          No past appointment summaries available
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2.5 max-h-full overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full w-full pr-5"
      data-testid="previous-appointments"
    >
      {appointments.map((appointment) => {
        return (
          <div
            className="flex bg-white border-b border-graph-border-color flex-row justify-between items-center w-full gap-2.5 py-2.5"
            key={appointment.id}
          >
            <div className="flex flex-row items-center gap-2.5 text-h5 text-btn-black font-semibold">
              <div>Appointment date:</div>

              <div>{formatDate(new Date(appointment.date))}</div>
            </div>

            <ButtonComponent
              testID="view-summary-button"
              onClick={() => handleClickViewSummary(appointment.id)}
              type="underline"
              className="mr-2.5"
            >
              View summary
            </ButtonComponent>
          </div>
        );
      })}

      {hasMore && (
        <div ref={loaderRef} className="py-2.5 flex justify-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};
